import { useQuery } from "@tanstack/react-query";
import { incomeOverviewApi } from "@/api/portfolioAdmin/incomeOverviewApi";
import { formatCurrency } from "@/design-system/lib/format";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelFigures, PanelRows } from "./shared";
import { financePanelsKeys } from "./financePanelsKeys";

/**
 * Finance panels: portfolio-wide, read-only money roll-ups that each drill
 * into /portfolio-admin/income-overview. They CONSUME the Income Overview API
 * (built by the finance workstream) — earnings Phoenix retains by source, and
 * outflows by recipient — over the current month window.
 */

const INCOME_OVERVIEW_ROUTE = "/portfolio-admin/income-overview";

export function FinanceEarningsBySourceWidget({ widget }: DashboardWidgetProps) {
  const overview = useQuery({
    queryKey: financePanelsKeys.earnings(),
    queryFn: () => incomeOverviewApi.get({ range: "month" }),
  });
  const data = overview.data;
  const sources = (data?.byTypePool ?? [])
    .filter((r) => r.group === "retained" || r.group === "vfi")
    .sort((a, b) => b.received - a.received);
  // Retained income + VFI (Vendor Fee Income is its own labeled stream — the
  // figure is spelled out so it never reads as pure retained earnings).
  const retained = data ? data.summary.retainedReceived + data.summary.vfiReceived : 0;

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to={INCOME_OVERVIEW_ROUTE}
      isLoading={overview.isLoading}
      isError={overview.isError}
      isEmpty={sources.length === 0}
      emptyText="No retained earnings recorded this month."
    >
      <div className="flex flex-col gap-space-3">
        <PanelFigures
          figures={[
            { label: "retained + VFI this month", value: formatCurrency(retained), tone: "ok" },
          ]}
        />
        <PanelRows
          items={sources.map((r) => ({
            id: r.key,
            primary: r.label,
            secondary: formatCurrency(r.received),
          }))}
        />
      </div>
    </WidgetFrame>
  );
}

export function FinanceOutflowsByRecipientWidget({ widget }: DashboardWidgetProps) {
  const overview = useQuery({
    queryKey: financePanelsKeys.outflows(),
    queryFn: () => incomeOverviewApi.get({ range: "month" }),
  });
  const recipients = (overview.data?.byRecipient ?? [])
    .filter((r) => r.group === "outflow" || r.group === "servicer")
    .sort((a, b) => b.received - a.received);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to={INCOME_OVERVIEW_ROUTE}
      isLoading={overview.isLoading}
      isError={overview.isError}
      isEmpty={recipients.length === 0}
      emptyText="No outflows recorded this month."
    >
      <PanelRows
        items={recipients.map((r) => ({
          id: r.key,
          primary: r.label,
          secondary: formatCurrency(r.received),
        }))}
      />
    </WidgetFrame>
  );
}
